import { WithLinks } from '@autopylot-internal/autopylot-api-client';
import { State } from '@autopylot-internal/state';
import { AircraftResponse, FleetManagmentClient } from './FleetManagmentClient.ts';
import { AircraftFormState } from './AircraftFormState.ts';
import { AlertPresenter } from '../app/AlertBinder.tsx';
import { Logger } from '../support/debug/Logger.ts';
import { MissionsState } from '../missions/model/MissionsState.ts';
import { EditableMissionState, IMission } from '../mission/model/EditableMissionState.ts';

export class FleetManagementState extends State<Record<string, never>> {
    static inject = () => [
        FleetManagmentClient,
        AircraftFormState,
        AlertPresenter,
        Logger
    ];
    constructor(
        private client: FleetManagmentClient,
        private formState: AircraftFormState,
        private alert: AlertPresenter,
        private logger: Logger
    ) {
        super({});
    }
    // using property injection to resolve circular reference
    missionsState!: MissionsState;

    fleet: AircraftResponse[] = [];
    loaded = false;

    alphaSort = (left: AircraftResponse, right: AircraftResponse) => {
        return left.nickname.localeCompare(right.nickname);
    };

    activeSort = (left: AircraftResponse, right: AircraftResponse) => {
        if (left.defaultType !== right.defaultType) {
            if (left.defaultType === 'primary') {
                return -1;
            }
            if (right.defaultType === 'primary') {
                return 1;
            }
            if (left.defaultType === 'backup') {
                return -1;
            }
            if (right.defaultType === 'backup') {
                return 1;
            }
        }
        return this.alphaSort(left, right);
    };

    private addAircraft = async (aircraft: Partial<AircraftResponse>) => {
        const model = await this.client.addAircraft(aircraft);
        this.fleet.push(model);
        this.setState({});
        return model;
    };

    updateAircraft = async (aircraft: AircraftResponse) => {
        const model = await this.client.updateAircraft(aircraft);
        this.fleet = [
            ...this.fleet.filter(a => a.aircraftId !== model.aircraftId),
            model
        ];
        this.setState({});
    };

    startAddingAircraft = async (onAdded?: (aircraft: AircraftResponse) => void) => {
        this.formState.start({
            form: {},
            onSave: async form => {
                const aircraft = await this.addAircraft(form);
                if (onAdded) {
                    onAdded(aircraft);
                }
            },
            isEditing: true,
            isOpen: true
        });
    };

    startViewingAircraft = async (aircraft: AircraftResponse) => {
        this.formState.start({
            form: { ...aircraft },
            onSave: form => this.updateAircraft(form as AircraftResponse),
            onDelete: () => this.deleteAircraft(aircraft),
        });
    };

    private deleteAircraft = async (aircraft: AircraftResponse) => {
        await this.client.deleteAircraft(aircraft);
        this.fleet = this.fleet.filter(a => a.aircraftId !== aircraft.aircraftId);
        this.setState({ });
    };

    ensureLoaded = async () => {
        if (!this.loaded) {
            this.fleet = await this.client.getFleet();
            this.loaded = true;
            this.setState({});
        }
    };

    refresh = async () => {
        this.fleet = await this.client.getFleet();
        this.loaded = true;
        this.setState({ });
    };

    getAircraft = (aircraftId: string): WithLinks<AircraftResponse> | undefined => {
        return this.fleet.find(a => a.aircraftId === aircraftId);
    };

    hasAnyAircraft = () => this.fleet.length > 0;

    getFleetForMission = (missionId: string) =>  {
        return this.fleet.filter(a => a.missions.includes(missionId)).sort(this.activeSort);
    };

    assignAircraftToMission = ({ aircraft, missionId }: { aircraft: AircraftResponse; missionId: string; }) => {
        if (aircraft.missions.includes(missionId)){
            return;
        }

        // assume success
        aircraft.missions.push(missionId);
        this.setState({ });

        Promise.resolve().then(async () => {
            await this.client.assignAircraftToMission({ aircraft, missionId });
        }).catch(err => {
            // try and refresh
            this.refresh().catch(() => {
                // do nothing we're already in an error state
            });
            this.alert.showAndLogError(err);
        });
    };

    removeAircraftFromMission = ({ aircraft, missionId }: { aircraft: AircraftResponse; missionId: string; }) => {
        if (!aircraft.missions.includes(missionId)){
            return;
        }

        // assume success
        aircraft.missions = aircraft.missions.filter(id => id !== missionId);
        this.setState({ });

        Promise.resolve().then(async () => {
            await this.client.removeAircraftFromMission({ aircraft, missionId });
        }).catch(err => {
            // try and refresh
            this.refresh().catch(() => {
                // do nothing we're already in an error state
            });
            this.alert.showAndLogError(err);
        });
    };

    getAllMissions = (aircraft: AircraftResponse) => {
        return aircraft.missions.map(missionId => this.missionsState.state.missions.find(m => m.state.missionId === missionId)).filter(mission => Boolean(mission)) as EditableMissionState[];
    };

    getPlannedMissions = (aircraft: AircraftResponse) => {
        return this.getAllMissions(aircraft).filter(m => !m.hasEnded());
    };

    getCompletedMissions = (aircraft: AircraftResponse) => {
        // not filtering out cancelled missions because on cancel the drone should have been removed from the mission
        // there is also the scenario where the mission was invalidated but not cancelled, but as far as we know, they still flew the mission
        // if they didn't cancel it
        return this.getAllMissions(aircraft).filter(m => m.hasEnded());
    };

    assignDefaultAircraftToNewMission = (mission: IMission) => {
        const defaultAircraft = this.fleet.filter(a => a.status === 'active' && Boolean(a.defaultType));
        if (!defaultAircraft.length) {
            // if no default aircraft, don't assign any
            return;
        }
        for (const aircraft of defaultAircraft){
            this.assignAircraftToMission({ aircraft, missionId: mission.missionId });
        }
    };

    areMissionsFullLoaded = () => {
        return this.missionsState.state.isFullyLoaded;
    };
}
